import { html } from "lit";

import { customElement, property, state } from "lit/decorators.js";

import { SignalElement, inject } from "ui-common/di";
import type { IAuthState, IAuthService } from "ui-common/services/auth/types";
import { type MenuButtonLabels, type MenuItem } from "ui-components/menu-button";
import { DataQueryController } from "ui-common/data-query-controller";
import type { GetNotificationsResult } from "api-contracts/rpc/notifications";
import { notificationsQueryOptions } from "ui-common/queries/notifications";

import { styles } from "./page-header.css";

import { navigateTo } from "../../router";
import { t, translate } from "../../translations/i18n";

import "ui-components/notification-bell";
import "ui-components/notification-modal/notification-modal";

import "../app-logo/app-logo";
import "../router-link/router-link";
import "../user-avatar/user-avatar";
import "@phosphor-icons/webcomponents/PhMagnifyingGlass";
import { LayoutController } from "ui-common";

@customElement("page-header")
export class PageHeader extends SignalElement {
  static override styles = styles;

  @inject<IAuthState>("authState")
  private authState!: IAuthState;

  @inject<IAuthService>("authService")
  private authService!: IAuthService;

  @property()
  override title: string = "";

  @state()
  private hasBackground = false;

  @state()
  private notificationModalOpen = false;

  private layoutController = new LayoutController(this);

  private notificationsController = new DataQueryController<GetNotificationsResult | null>(
    this,
    () => notificationsQueryOptions,
  );

  private handleSlotChange = (e: Event) => {
    const slot = e.target as HTMLSlotElement;
    const assignedNodes = slot.assignedNodes();
    this.hasBackground = assignedNodes.length > 0;
  };

  private get menuItems(): MenuItem[] {
    const user = this.authState.currentUser.get();
    if (!user) return [];

    const roles = user.roles || [];
    const isAdmin = roles.includes("admin");
    const isCreator = Boolean(user.handle);
    const isFan = !isCreator;
    const isMobile = this.layoutController.isMobile;

    const menuItems: MenuItem[] = [];

    if (isAdmin) {
      menuItems.push({
        id: "admin",
        href: "/admin",
        icon: html`<ph-beer-stein weight="bold" size="24"></ph-beer-stein>`,
        label: t("menu.admin"),
      });
    }

    if (isMobile) {
      if (isFan) {
        menuItems.push({
          id: "sign-in",
          href: "onboard-creator?step=1",
          label: t("menu.becomeCreator"),
          icon: html`<ph-crown weight="bold" size="20"></ph-crown>`,
        });
      }

      if (!isFan) {
        menuItems.push({
          id: "analytics",
          href: "analytics",
          icon: html`<ph-chart-bar weight="bold" size="24"></ph-chart-bar>`,
          label: t("menu.analytics"),
        });
      }

      menuItems.push({
        id: "settings",
        href: "settings",
        icon: html`<ph-gear weight="bold" size="24"></ph-gear>`,
        label: t("menu.accountSettings"),
      });
    }

    return menuItems;
  }

  private get menuLabels(): MenuButtonLabels {
    return {
      openMenu: t("accessibility.openMenu"),
      viewProfile: t("user.viewProfile"),
      signIn: t("sign-in.signIn"),
      selectEnglish: t("language.selectEnglish"),
      selectDutch: t("language.selectDutch"),
      lightTheme: t("theme.lightTheme"),
      darkTheme: t("theme.darkTheme"),
      manage: t("menu.manage"),
      signOut: t("menu.signOut"),
      becomeCreator: t("menu.becomeCreator"),
    };
  }

  private handleMenuNavigate = (event: CustomEvent) => {
    const { href } = event.detail;

    if (href === "/admin" && this.authState.currentUser.get()?.roles.includes("admin")) {
      window.open(href, "_self", "noopener,noreferrer");
      return;
    }

    navigateTo(href);
  };

  private handleViewProfile = () => {
    const user = this.authState.currentUser.get();
    if (!user?.handle) return;

    window.open(`/${user.handle}`, "_self", "noopener,noreferrer");
  };

  private handleSignOut = () => {
    this.authService.signOut();
  };

  private handleNotificationBellClick = () => {
    this.notificationModalOpen = true;
  };

  private handleNotificationModalClose = () => {
    this.notificationModalOpen = false;
  };

  override render() {
    const user = this.authState.currentUser.get();
    const memberSinceYear = user
      ? translate("user.memberSince", {
          year: user?.memberSinceDate ? new Date(user.memberSinceDate).getFullYear().toString() : "unknown",
        })
      : "";

    const notificationsData = this.notificationsController?.result?.data as GetNotificationsResult | null | undefined;
    const notifications = notificationsData?.notifications || [];

    return html`
      <div class="header-background">
        <slot name="background" @slotchange=${this.handleSlotChange}></slot>
      </div>
      <header class="${this.hasBackground ? "with-background" : ""}">
        <div class="logo-title-container">
          <div class="logo-container">
            <app-logo class="show-on-mobile"></app-logo>
          </div>
          ${this.title !== "" ? html`<h1 class="page-title">${this.title}</h1>` : html`<slot name="title"></slot>`}
        </div>
        <div class="buttons-wrapper">
          <div class="menu-controls">
            <notification-bell @click=${this.handleNotificationBellClick}></notification-bell>
            <menu-button
              .userName=${user?.handle || ""}
              .userHandle=${user?.handle}
              .memberSinceYear=${memberSinceYear}
              .userAvatar=${user?.avatarUrl}
              .avatarComponent=${html`<user-avatar size="large"></user-avatar>`}
              .menuItems=${this.menuItems}
              .userId=${user?.id}
              .labels=${this.menuLabels}
              @sign-out=${this.handleSignOut}
              @view-profile=${this.handleViewProfile}
              @menu-navigate=${this.handleMenuNavigate}
            ></menu-button>
          </div>
          <div class="action-buttons">
            <slot></slot>
          </div>
        </div>
      </header>
      <notification-modal
        .open=${this.notificationModalOpen}
        .notifications=${notifications}
        @close=${this.handleNotificationModalClose}
      >
        <span slot="title">${t("notificationCenter.title")}</span>
        <span slot="subtitle">${t("notificationCenter.subtitle")}</span>
        <span slot="read">${t("notificationCenter.read")}</span>
        <span slot="unread">${t("notificationCenter.unread")}</span>
      </notification-modal>
    `;
  }
}
